import {
  baseKeymap,
  chainCommands,
  createParagraphNear,
  liftEmptyBlock,
  newlineInCode,
  splitBlock,
} from "prosemirror-commands";
import { dropCursor } from "prosemirror-dropcursor";
import { gapCursor } from "prosemirror-gapcursor";
import { history, redo, undo } from "prosemirror-history";
import { keymap } from "prosemirror-keymap";
import {
  liftListItem,
  sinkListItem,
  splitListItem,
} from "prosemirror-schema-list";
import {
  type Command,
  Plugin,
  TextSelection,
  type Transaction,
} from "prosemirror-state";
import { isInTable, tableEditing } from "prosemirror-tables";
import type { EditorView } from "prosemirror-view";

import { EDITOR_MODE } from "../constants";
import {
  EDITOR_V3_DROP_CURSOR_CLASS,
  EDITOR_V3_DROP_CURSOR_COLOR,
  EDITOR_V3_DROP_CURSOR_WIDTH,
} from "../drop-cursor";
import { EDITOR_V3_OPEN_LINK_OPTIONS_EVENT } from "../editor-events";
import { focusSchema } from "../schema";
import { editorClipboardPlugin } from "./clipboard";
import { editorDropPlugin } from "./drop";
import { editorInputRulesPlugin } from "./input-rules";
import { markdownModePlugin } from "./markdown-mode";
import type { EditorSuggestionState } from "./suggestions";
import { editorSuggestionsPlugin } from "./suggestions";
import { editorTableControlsPlugin } from "./table-controls";

const insertHardBreak =
  (mode: EDITOR_MODE): Command =>
  (state, dispatch) => {
    if (mode === EDITOR_MODE.SIMPLE) {
      dispatch?.(
        state.tr
          .insertText("\\")
          .replaceSelectionWith(focusSchema.nodes.hard_break.create())
          .scrollIntoView(),
      );
      return true;
    }

    dispatch?.(
      state.tr
        .replaceSelectionWith(focusSchema.nodes.hard_break.create())
        .scrollIntoView(),
    );
    return true;
  };

const selectEmptyParagraphAfterPosition = (
  tr: Transaction,
  positionAfterBlock: number,
) => {
  const paragraph = focusSchema.nodes.paragraph;
  const nextNode = tr.doc.resolve(positionAfterBlock).nodeAfter;
  const selectionPosition = positionAfterBlock + 1;

  if (nextNode?.type !== paragraph || nextNode.content.size > 0) {
    tr.insert(positionAfterBlock, paragraph.create());
  }

  tr.setSelection(TextSelection.create(tr.doc, selectionPosition));
};

const selectEmptyParagraphAfter =
  (depth: number): Command =>
  (state, dispatch) => {
    const tr = state.tr;

    selectEmptyParagraphAfterPosition(tr, state.selection.$from.after(depth));
    dispatch?.(tr.scrollIntoView());
    return true;
  };

const exitCodeBlockAfterBlankLine: Command = (state, dispatch) => {
  const { $anchor, $head } = state.selection;

  if (
    !$head.sameParent($anchor) ||
    !$head.parent.type.spec.code ||
    $head.parentOffset !== $head.parent.content.size
  ) {
    return false;
  }

  const textBeforeCursor = $head.parent.textBetween(0, $head.parentOffset);

  if (!textBeforeCursor.endsWith("\n\n")) {
    return false;
  }

  if (dispatch) {
    const positionAfterCodeBlock = $head.after($head.depth);
    const tr = state.tr.delete($head.pos - 2, $head.pos);

    selectEmptyParagraphAfterPosition(
      tr,
      tr.mapping.map(positionAfterCodeBlock, -1),
    );
    dispatch(tr.scrollIntoView());
  }

  return true;
};

const exitTableFromLastRow: Command = (state, dispatch, view) => {
  if (!state.selection.empty || !isInTable(state)) {
    return false;
  }

  const { $from } = state.selection;
  let tableDepth: number | null = null;

  for (let depth = $from.depth; depth > 0; depth--) {
    if ($from.node(depth).type.name === "table") {
      tableDepth = depth;
      break;
    }
  }

  if (tableDepth == null) {
    return false;
  }

  const table = $from.node(tableDepth);
  const rowIndex = $from.index(tableDepth);

  if (rowIndex !== table.childCount - 1) {
    return false;
  }

  return selectEmptyParagraphAfter(tableDepth)(state, dispatch, view);
};

type MarkdownListLine = {
  content: string;
  marker: string;
  nextMarker: string;
};

const markdownListLine = (line: string): MarkdownListLine | null => {
  const taskMatch = /^(\s*)([-+*])\s+\[( |x|X)\]\s+(.*)$/.exec(line);

  if (taskMatch) {
    const marker = `${taskMatch[1]}${taskMatch[2]} [ ] `;

    return {
      content: taskMatch[4],
      marker: line.slice(0, line.length - taskMatch[4].length),
      nextMarker: marker,
    };
  }

  const orderedMatch = /^(\s*)(\d+)([.)])(\s+)(.*)$/.exec(line);

  if (orderedMatch) {
    return {
      content: orderedMatch[5],
      marker: line.slice(0, line.length - orderedMatch[5].length),
      nextMarker: `${orderedMatch[1]}${Number(orderedMatch[2]) + 1}${
        orderedMatch[3]
      }${orderedMatch[4]}`,
    };
  }

  const bulletMatch = /^(\s*)([-+*])(\s+)(.*)$/.exec(line);

  if (!bulletMatch) {
    return null;
  }

  return {
    content: bulletMatch[4],
    marker: line.slice(0, line.length - bulletMatch[4].length),
    nextMarker: `${bulletMatch[1]}${bulletMatch[2]}${bulletMatch[3]}`,
  };
};

const continueMarkdownSourceList: Command = (state, dispatch) => {
  if (!state.selection.empty) {
    return false;
  }

  const { $from } = state.selection;

  if ($from.parent.type !== focusSchema.nodes.paragraph) {
    return false;
  }

  const source = $from.parent.textBetween(
    0,
    $from.parent.content.size,
    "\n",
    "\n",
  );
  const cursorOffset = $from.parentOffset;
  const lineStartOffset = source.lastIndexOf("\n", cursorOffset - 1) + 1;
  const lineEndIndex = source.indexOf("\n", cursorOffset);
  const lineEndOffset = lineEndIndex >= 0 ? lineEndIndex : source.length;
  const line = source.slice(lineStartOffset, lineEndOffset);
  const listLine = markdownListLine(line);

  if (!listLine) {
    return false;
  }

  const cursorOffsetInLine = cursorOffset - lineStartOffset;

  if (cursorOffsetInLine < listLine.marker.length) {
    return false;
  }

  const lineContentBeforeCursor = line.slice(
    listLine.marker.length,
    cursorOffsetInLine,
  );
  const lineContentAfterCursor = line.slice(cursorOffsetInLine);
  const lineStartPosition = $from.start() + lineStartOffset;
  const lineEndPosition = $from.start() + lineEndOffset;

  if (!lineContentBeforeCursor && !lineContentAfterCursor) {
    if (!dispatch) {
      return true;
    }

    const tr = state.tr;

    if (lineStartOffset === 0 && lineEndOffset === source.length) {
      tr.delete(lineStartPosition, lineEndPosition);
      tr.setSelection(
        TextSelection.create(tr.doc, tr.mapping.map(lineStartPosition)),
      );
    } else {
      tr.delete(
        lineStartOffset > 0 ? lineStartPosition - 1 : lineStartPosition,
        lineEndPosition,
      );
      const paragraphPosition = tr.mapping.map($from.after($from.depth), -1);

      tr.insert(paragraphPosition, focusSchema.nodes.paragraph.create());
      tr.setSelection(TextSelection.create(tr.doc, paragraphPosition + 1));
    }

    dispatch(tr.scrollIntoView());
    return true;
  }

  if (dispatch) {
    const tr = state.tr.replaceSelectionWith(
      focusSchema.nodes.hard_break.create(),
    );

    tr.insertText(listLine.nextMarker);
    dispatch(tr.scrollIntoView());
  }

  return true;
};

const insertLineBreak =
  (mode: EDITOR_MODE): Command =>
  (state, dispatch, view) => {
    if (mode === EDITOR_MODE.RICH) {
      return chainCommands(
        exitCodeBlockAfterBlankLine,
        newlineInCode,
        splitListItem(focusSchema.nodes.list_item),
        exitTableFromLastRow,
        createParagraphNear,
        liftEmptyBlock,
        splitBlock,
      )(state, dispatch, view);
    }

    return chainCommands(newlineInCode, continueMarkdownSourceList, splitBlock)(
      state,
      dispatch,
      view,
    );
  };

const sinkListItemCommand = sinkListItem(focusSchema.nodes.list_item);
const liftListItemCommand = liftListItem(focusSchema.nodes.list_item);

const editorLinkInteractionPlugin = () =>
  new Plugin({
    props: {
      handleDOMEvents: {
        click: (view, event) => {
          const target = event.target;
          const link =
            target instanceof HTMLElement
              ? target.closest<HTMLAnchorElement>("a[href]")
              : null;

          if (!link) {
            return false;
          }

          event.preventDefault();

          const clickedPosition = view.posAtCoords({
            left: event.clientX,
            top: event.clientY,
          })?.pos;
          const domPosition =
            link.firstChild instanceof Node
              ? view.posAtDOM(link.firstChild, 0)
              : view.posAtDOM(link, 0);
          const position = clickedPosition ?? domPosition;
          const safePosition = Math.max(
            1,
            Math.min(position, view.state.doc.content.size),
          );

          view.dispatch(
            view.state.tr.setSelection(
              TextSelection.create(view.state.doc, safePosition),
            ),
          );
          view.dom.dispatchEvent(
            new CustomEvent(EDITOR_V3_OPEN_LINK_OPTIONS_EVENT, {
              bubbles: true,
            }),
          );

          return true;
        },
      },
    },
  });

export const buildEditorPlugins = ({
  mode,
  suggestions,
  uploadImageFile,
}: {
  mode: EDITOR_MODE;
  suggestions?: {
    onChange?: (state: EditorSuggestionState, view: EditorView) => void;
    onKeyDown?: (
      event: KeyboardEvent,
      state: NonNullable<EditorSuggestionState>,
      view: EditorView,
    ) => boolean;
  };
  uploadImageFile?: (file: File) => Promise<string | null>;
}): Plugin[] => [
  history(),
  editorSuggestionsPlugin(suggestions),
  keymap({
    "Mod-y": redo,
    "Mod-z": undo,
    "Shift-Enter": insertHardBreak(mode),
    Enter: insertLineBreak(mode),
    Tab: mode === EDITOR_MODE.RICH ? sinkListItemCommand : () => false,
    "Shift-Tab": mode === EDITOR_MODE.RICH ? liftListItemCommand : () => false,
  }),
  keymap(baseKeymap),
  ...(mode === EDITOR_MODE.RICH ? [editorInputRulesPlugin()] : []),
  dropCursor({
    class: EDITOR_V3_DROP_CURSOR_CLASS,
    color: EDITOR_V3_DROP_CURSOR_COLOR,
    width: EDITOR_V3_DROP_CURSOR_WIDTH,
  }),
  gapCursor(),
  tableEditing({ allowTableNodeSelection: true }),
  editorLinkInteractionPlugin(),
  editorTableControlsPlugin(),
  editorClipboardPlugin({ mode, uploadImageFile }),
  editorDropPlugin({ mode, uploadImageFile }),
  markdownModePlugin(mode),
];
